// Real property data — Coburg Home (SIL).
// Drop more photos at /public/homes/coburg/6.jpg, 7.jpg, … and just
// extend the `images` array below to have them flow into the gallery.
//
// Copy discipline: Reframed 2026-02 to remove luxury real-estate wording
// ("penthouse", "premium", "spa", "marble", "chef's kitchen", "high-end").
// This is an NDIS SIL home — copy centres accessibility, calm, dignity.

export const COBURG = {
  slug: "coburg-retreat",
  id: "prop-coburg-retreat",
  href: "/homes/coburg-retreat",
  name: "The Coburg Home",
  suburb: "Coburg",
  city: "melbourne",
  state: "VIC",
  postcode: "3058",
  region: "Inner north Melbourne",
  country: "AU",
  lat: -37.7434,
  lon: 144.9660,
  short: "SIL Home · Coburg, VIC",

  published: true,
  status: "available",              // legacy field — kept for existing rendering
  listing_status: "available",      // new canonical status
  recordVerificationStatus: "verified",
  vacancy_available: true,
  vacancies: 2,
  total_bedrooms: 3,
  participant_bedrooms: 2,
  occupied_bedrooms: 0,
  ooa_room: true,
  gender_preference: null,
  preferred_participant_profile: "Adults seeking a warm, calm, step-free home with 1:1 or 1:2 close support.",
  available_from: "2026-02-01",
  vacancy_last_updated: "2026-02-15",

  support_ratios: ["1:1", "1:2"],
  support_model: ["24-7", "sleepover", "individualised"],
  sda_category: null,
  accessibility_features: [
    "step-free-entry", "wide-doorways", "accessible-bathroom", "sensory-friendly", "secure-outdoor",
  ],
  suitability_tags: [
    "psychosocial-disability", "physical-disability", "intellectual-disability",
    "sensory-friendly", "high-intensity",
  ],

  property_type: "townhouse",
  bathrooms: 2,
  living_areas: 2,
  outdoor_area: true,
  secure_yard: true,
  parking: 1,
  garage: false,
  nearby: {
    transport: "Upfield train line · Route 19 tram",
    shops: "Sydney Road shopping strip · Pentridge Coburg Square",
    medical: "Multiple GP clinics & allied-health within 5 mins",
    community: "Coburg Lake · Bridges Reserve · community centres",
  },

  internal: {
    sda_provider: "",
    property_owner: "Alpha (leased)",
    referral_source: "direct",
    landlord: "",
    management_notes: "",
    contact: { name: "Intake team", email: "SIL@alphacommunitycare.com.au", phone: "1300 380 692" },
  },

  // Short, scannable headline copy — no fluff, no luxury framing.
  headline: "A warm, step-free SIL home in the heart of Coburg.",
  oneLiner:
    "A calm, light-filled Alpha SIL home in inner-north Melbourne — designed around participant routines, not architectural glamour.",

  // Core chips shown at a glance
  badges: [
    { label: "SIL Home", tone: "purple" },
    { label: "1:1 & 1:2 support", tone: "green" },
    { label: "Registered NDIS", tone: "neutral" },
  ],

  specs: [
    { k: "Service", v: "Supported Independent Living" },
    { k: "Support ratio", v: "1:1 and 1:2 only" },
    { k: "Location", v: "Coburg, VIC" },
    { k: "Availability", v: "Enquire for current capacity" },
    { k: "Registered", v: "NDIS provider · Practice Standards aligned" },
    { k: "Support hours", v: "24/7 on-call" },
  ],

  // Feature chips — accessibility- and everyday-living-first.
  features: [
    "Step-free entry",
    "Wide internal doorways",
    "Accessible bathroom",
    "Sensory-considered finishes",
    "Even LED lighting",
    "Timber flooring for calm acoustics",
    "Open-plan living & dining",
    "Full kitchen with gas cooktop",
    "Integrated dishwasher",
    "Separate laundry amenities",
    "Private, secure entry",
    "Close to public transport",
  ],

  // NDIS / service delivery blurbs
  ndis: {
    summary:
      "Delivered under the NDIS as Supported Independent Living (SIL). The Coburg Home suits participants who benefit from a calm, accessible environment with close, consistent support.",
    points: [
      "Services delivered as 1:1 or 1:2 support ratios only — never higher.",
      "Staff matched to each resident's preferences, culture and routines.",
      "Overnight and 24/7 on-call availability.",
      "Coordinated clinical oversight on request (nursing / allied health partners).",
      "Plain-English service agreements and regular reviews.",
    ],
  },

  // Rich property description — accurate to visible photos, no luxury framing.
  description: [
    "The Coburg Home is a warm, thoughtfully finished SIL home in inner-north Melbourne, set up so participants can move in and get straight into their routines.",
    "The open-plan living and dining space uses timber flooring and even LED lighting to keep the acoustics soft and the sensory load low — a calmer place to spend the day than a clinical setting.",
    "The kitchen is fully appointed — white cabinetry, a gas cooktop with rangehood and an integrated dishwasher — so day-to-day cooking is practical and safe.",
    "Two bathrooms let 1:1 and 1:2 routines run without waiting, and the second amenity carries the laundry sink so mornings don't back up.",
    "The result is a calm, accessible home that supports dignity in every part of the day.",
  ],

  // Real-estate-style quick facts row (accurate to visible photos only).
  quickFacts: [
    { Icon: "Layers", k: "Layout", v: "2 levels" },
    { Icon: "Bath", k: "Bathrooms", v: "2" },
    { Icon: "Users", k: "Support ratio", v: "1:1 & 1:2 only" },
    { Icon: "Clock", k: "On-call", v: "24 / 7" },
    { Icon: "Calendar", k: "Available", v: "Now" },
  ],

  images: [
    { src: "/homes/coburg/1.jpg", alt: "Open-plan living with timber floors and even lighting at the Coburg SIL home" },
    { src: "/homes/coburg/2.jpg", alt: "Second amenity — shower, toilet and laundry sink for supported daily routines" },
    { src: "/homes/coburg/3.jpg", alt: "Accessible bathroom with bath and separate shower option" },
    { src: "/homes/coburg/4.jpg", alt: "Kitchen with white cabinetry, gas cooktop and integrated dishwasher opening to dining" },
    { src: "/homes/coburg/5.jpg", alt: "Main bathroom with stone vanity, bath and shower amenity" },
  ],

  // Lifestyle storytelling sections
  story: {
    designedFor: {
      eyebrow: "Designed for calm",
      title: "A home that feels lived-in from day one.",
      body: "Warm timber. Generous natural light. Carefully chosen finishes that quiet the senses. Coburg Home was set up so participants can settle straight into their routines — not adapt to a clinical space.",
      image: "/homes/coburg/1.jpg",
    },
    indoorOutdoor: {
      eyebrow: "Everyday living",
      title: "Open-plan living, kitchen at the centre.",
      body: "A full kitchen flows into the dining and lounge — practical for shared meals, family visits, and time spent together. Multiple sit-down moments across the level mean residents are never crowded.",
      image: "/homes/coburg/4.jpg",
    },
    selfCare: {
      eyebrow: "Dignified self-care",
      title: "Two accessible bathrooms, calm daily routines.",
      body: "Two full amenities — bath and shower option, laundry sink close at hand — so 1:1 and 1:2 morning routines can run without waiting. Everyday living, made gentle.",
      image: "/homes/coburg/3.jpg",
    },
  },

  accessibility: [
    { title: "Step-free entry", text: "Smooth transitions between rooms — no awkward thresholds." },
    { title: "Wide internal doorways", text: "Comfortable for mobility aids and assistance throughout the home." },
    { title: "Accessible amenities", text: "Two bathrooms with grab-rail-ready walls and roll-in shower options." },
    { title: "Even lighting", text: "Recessed LED throughout — soft on sensory needs, glare-free." },
    { title: "Quiet acoustics", text: "Timber + soft furnishings absorb sound and lower in-home stress." },
    { title: "Privacy by design", text: "Resident bedrooms zoned away from shared living + active areas." },
  ],

  idealFor: [
    "Adults seeking a calm, warm home environment",
    "Participants benefitting from 1:1 or 1:2 close support",
    "People who value private bathrooms and practical layouts",
    "Sensory-aware participants — low-stim, well-lit interiors",
    "Anyone seeking a respectful, dignified place to live",
  ],

  whyLove: [
    "It looks like a real home — not a service.",
    "The kitchen invites family in.",
    "You can hear yourself think.",
    "Bathrooms support real daily routines.",
  ],

  lifestyle: {
    headline: "Inner-north Melbourne, on your doorstep.",
    blurb:
      "Coburg's tree-lined streets sit minutes from cafés, parks, the Sydney Road shopping strip, Pentridge Coburg Square, and the Upfield train line.",
    points: [
      { k: "Cafés & dining", v: "Sydney Road · Pentridge precinct" },
      { k: "Green space", v: "Coburg Lake · Bridges Reserve" },
      { k: "Transport", v: "Upfield line · 19 tram · easy CBD access" },
      { k: "Healthcare", v: "Multiple GP & allied health within minutes" },
    ],
  },
};
